import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/useAuth";
import { PLAYBOOK_SECTIONS, PLAYBOOK_VERSION, TIER_LABEL, filterByTier, type PlaybookTier } from "@/data/partnerPlaybook";
import PlaybookSectionView from "@/components/partner-hub/PlaybookSectionView";

const TIER_TABS: (PlaybookTier | "all")[] = ["all", "advocate", "partner", "anchor"];

export default function PartnerHandbook() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [tier, setTier] = useState<PlaybookTier | "all">("all");
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!loading && !user) navigate("/auth?redirect=/partners/handbook");
  }, [loading, user, navigate]);

  const visible = useMemo(() => {
    const base = filterByTier(PLAYBOOK_SECTIONS, tier);
    if (!query.trim()) return base;
    const q = query.toLowerCase();
    return base.filter((s) => JSON.stringify(s).toLowerCase().includes(q));
  }, [tier, query]);

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="pt-28 pb-20 px-4 sm:px-6 max-w-7xl mx-auto">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
          <div>
            <Badge variant="outline" className="mb-3">Playbook · v{PLAYBOOK_VERSION}</Badge>
            <h1 className="font-display text-3xl sm:text-4xl font-bold">Partner &amp; Advocate Playbook</h1>
            <p className="text-muted-foreground mt-2 max-w-2xl">
              The operating field guide for Advocates, Partners, and Anchors. Filter by your tier, search, or download the printable field guide for meetings.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button asChild variant="outline" size="sm">
              <a href="/handbook/emrkt-partner-field-guide.pdf" target="_blank" rel="noreferrer">Download field guide (PDF)</a>
            </Button>
            <Button asChild size="sm">
              <Link to="/partner">Go to Partner Hub</Link>
            </Button>
          </div>
        </div>

        {/* Filter row */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="flex flex-wrap gap-1.5 rounded-md border border-border bg-card p-1">
            {TIER_TABS.map((t) => (
              <button
                key={t}
                onClick={() => setTier(t)}
                className={`px-3 py-1.5 text-xs rounded-sm transition ${
                  tier === t ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {t === "all" ? "All tiers" : TIER_LABEL[t]}
              </button>
            ))}
          </div>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search the playbook…"
            className="flex-1 min-w-[200px] max-w-md bg-card border border-border rounded-md px-3 py-1.5 text-sm"
          />
        </div>

        <div className="grid lg:grid-cols-[240px,1fr] gap-8">
          {/* Sticky sidebar TOC */}
          <aside className="lg:sticky lg:top-24 lg:self-start">
            <Card className="p-3">
              <div className="text-xs uppercase tracking-wide text-muted-foreground px-2 py-1">Contents</div>
              <ul className="space-y-0.5">
                {visible.map((s) => (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      className="block px-2 py-1.5 text-sm text-foreground/80 hover:text-primary hover:bg-muted/50 rounded"
                    >
                      {s.title}
                    </a>
                  </li>
                ))}
                {visible.length === 0 && (
                  <li className="px-2 py-2 text-xs text-muted-foreground">No sections match.</li>
                )}
              </ul>
            </Card>
          </aside>

          {/* Sections */}
          <div className="space-y-10 min-w-0">
            {visible.map((s) => (
              <PlaybookSectionView key={s.id} section={s} />
            ))}

            {visible.length > 0 && (
              <div className="pt-8 border-t border-border flex flex-wrap gap-3">
                <Button asChild>
                  <Link to="/partners/apply">Apply to the program</Link>
                </Button>
                <Button asChild variant="outline">
                  <Link to="/partner">Go to Partner Hub</Link>
                </Button>
                <Button asChild variant="ghost">
                  <Link to="/partners/playbook">Public preview page</Link>
                </Button>
              </div>
            )}
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}